import type { ReactNode } from "react";
import type { Entity } from "@common/types/entity";
import type { AvatarGroupSize } from "@design-system/Avatar/AvatarGroup";
import { AvatarButton } from "./AvatarButton";
import { AvatarTooltip } from "./AvatarTooltip";
import { EntityAvatar, type EntityAvatarEntity } from "./EntityAvatar";
import { getEntityAvatarName } from "@helpers/entityAvatarName";
import { LabelledAvatarGroup } from "./LabelledAvatarGroup";

export type EntityAvatarGroupEntity = EntityAvatarEntity & Pick<Entity, "id">;

export interface EntityAvatarGroupProps<T extends EntityAvatarGroupEntity> {
    entities: readonly T[];
    /** Visible avatars before the +N chip (see `AvatarGroup`). @default 4 */
    max?: number;
    /** @default "xs" */
    size?: AvatarGroupSize;
    /** Trailing text after the stack, e.g. "3 entities". */
    label?: ReactNode;
    /** Makes each avatar a button ("Open {name}") that calls back with its entity. */
    onEntityClick?: (entity: T) => void;
    className?: string;
}

/**
 * A stack of entity avatars, each with its name in a tooltip and a
 * right-click copy of its ID.
 */
export const EntityAvatarGroup = <T extends EntityAvatarGroupEntity>({
    entities,
    max,
    size,
    label,
    onEntityClick,
    className,
}: Readonly<EntityAvatarGroupProps<T>>) => (
    <LabelledAvatarGroup
        items={entities}
        getKey={(entity) => entity.id}
        getLabel={getEntityAvatarName}
        renderAvatar={(entity) => (
            <AvatarTooltip content={getEntityAvatarName(entity)}>
                {onEntityClick === undefined ? (
                    <EntityAvatar entity={entity} copyId={entity.id} />
                ) : (
                    <AvatarButton
                        name={getEntityAvatarName(entity)}
                        onClick={() => onEntityClick(entity)}
                    >
                        <EntityAvatar
                            entity={entity}
                            copyId={entity.id}
                            aria-hidden
                        />
                    </AvatarButton>
                )}
            </AvatarTooltip>
        )}
        max={max}
        size={size}
        label={label}
        className={className}
    />
);
